<div class="flex flex-col gap-4 p-4">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">Finance Dashboard</h1>
      <p class="text-secondary text-sm">AR, AP, budget and collection intelligence</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- AR + AP Summary Row -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <!-- AR Summary -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Accounts Receivable (<span class="currency normal-case">&#xe001;</span>)</h3>
      @if (!hasArPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingAr) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (arData) {
        <div class="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div class="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Opening Balance</div>
            <div class="mt-1 break-words text-sm font-bold text-gray-800">{{ arData.opening_balance | number: '1.2-2' }}</div>
          </div>
          <div class="min-w-0 rounded-lg border border-blue-100 bg-blue-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Invoices</div>
            <div class="mt-1 break-words text-sm font-bold text-blue-700">{{ arData.charge | number: '1.2-2' }}</div>
          </div>
          <div class="min-w-0 rounded-lg border border-green-100 bg-green-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Receipts</div>
            <div class="mt-1 break-words text-sm font-bold text-green-700">{{ arData.payment | number: '1.2-2' }}</div>
          </div>
          <div class="min-w-0 rounded-lg border border-orange-100 bg-orange-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Balance</div>
            <div class="mt-1 break-words text-sm font-bold text-orange-700">{{ arData.closing_balance | number: '1.2-2' }}</div>
          </div>
        </div>
        @if (arData.top_accounts?.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Top Accounts by Balance</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <!-- Header row -->
          <div class="mb-0.5 grid grid-cols-[1fr_80px_80px_80px_90px] px-2">
            <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Account</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Opening</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Inv.</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Rec.</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Balance</span>
          </div>
          <div class="divide-y divide-gray-50">
            @for (a of arData.top_accounts; track $index) {
              <div class="grid grid-cols-[1fr_80px_80px_80px_90px] items-center rounded px-2 py-2 transition-colors hover:bg-gray-50">
                <span class="truncate text-xs font-medium text-gray-700">{{ a.name }}</span>
                <span class="text-right text-xs text-gray-500">{{ a.opening_balance | number: '1.2-2' }}</span>
                <span class="text-right text-xs text-blue-600">{{ a.charge | number: '1.2-2' }}</span>
                <span class="text-right text-xs text-green-600">{{ a.payment | number: '1.2-2' }}</span>
                <span class="text-right text-xs font-semibold text-orange-600">{{ a.closing_balance | number: '1.2-2' }}</span>
              </div>
            }
          </div>
        }
      }
    </div>

    <!-- AP Summary -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Accounts Payable (<span class="currency normal-case">&#xe001;</span>)</h3>
      @if (!hasApPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingAp) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (apData) {
        <div class="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <div class="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Opening Balance</div>
            <div class="mt-1 break-words text-sm font-bold text-gray-800">{{ apData.opening_balance | number: '1.2-2' }}</div>
          </div>
          <div class="min-w-0 rounded-lg border border-blue-100 bg-blue-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Bill Amount</div>
            <div class="mt-1 break-words text-sm font-bold text-blue-700">{{ apData.bill | number: '1.2-2' }}</div>
          </div>
          <div class="min-w-0 rounded-lg border border-green-100 bg-green-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Payment</div>
            <div class="mt-1 break-words text-sm font-bold text-green-700">{{ apData.payment | number: '1.2-2' }}</div>
          </div>
          <div class="min-w-0 rounded-lg border border-orange-100 bg-orange-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Balance</div>
            <div class="mt-1 break-words text-sm font-bold text-orange-700">{{ apData.payable | number: '1.2-2' }}</div>
          </div>
          <div class="min-w-0 rounded-lg border border-yellow-100 bg-yellow-50 p-3 text-center">
            <div class="text-secondary mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wide">Pending Approval Bills ({{ apData.pending_approval_bills ?? 0 }})</div>
            <div class="mt-1 break-words text-sm font-bold text-yellow-700">{{ apData.pending_approval_bills_amount | number: '1.2-2' }}</div>
          </div>
        </div>
        @if (apData.top_vendors?.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Top Vendors by Payables</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <!-- Header row -->
          <div class="mb-0.5 grid grid-cols-[1fr_80px_80px_80px_90px] px-2">
            <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Vendor</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Opening</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Bill</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Payment</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Balance</span>
          </div>
          <div class="divide-y divide-gray-50">
            @for (v of apData.top_vendors; track $index) {
              <div class="grid grid-cols-[1fr_80px_80px_80px_90px] items-center rounded px-2 py-2 transition-colors hover:bg-gray-50">
                <span class="truncate text-xs font-medium text-gray-700">{{ v.name }}</span>
                <span class="text-right text-xs text-gray-500">{{ v.opening_balance | number: '1.2-2' }}</span>
                <span class="text-right text-xs text-blue-600">{{ v.bill | number: '1.2-2' }}</span>
                <span class="text-right text-xs text-green-600">{{ v.payment | number: '1.2-2' }}</span>
                <span class="text-right text-xs font-semibold text-orange-600">{{ v.payable | number: '1.2-2' }}</span>
              </div>
            }
          </div>
        }
      }
    </div>
  </div>
  <!-- end AR+AP grid -->

  <!-- Middle Row 2: Collection Performance + Budget vs Actual + Payment Methods -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <!-- Collection Performance -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-start justify-between gap-2">
        <h3 class="text-secondary min-w-0 text-sm font-semibold uppercase tracking-wide">Collection Performance</h3>
        @if (collectionData) {
          <span
            class="shrink-0 whitespace-nowrap text-right text-sm font-bold"
            [ngClass]="(collectionData.collection_pct ?? 0) >= 80 ? 'text-green-600' : (collectionData.collection_pct ?? 0) >= 60 ? 'text-yellow-600' : 'text-red-600'"
          >
            {{ collectionData.collection_pct ?? 0 }}% collected
          </span>
        }
      </div>
      @if (!hasCollectionPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingCollection) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (collectionData) {
        <div class="mb-4 grid grid-cols-2 gap-3">
          <div class="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-3 text-center">
            <div class="text-secondary break-words text-[10px] font-semibold uppercase tracking-wide">Opening Balance</div>
            <div class="mt-0.5 break-words text-sm font-bold text-gray-700" [innerHTML]="collectionData.opening_balance | amount"></div>
          </div>
          <div class="min-w-0 rounded-lg border border-blue-100 bg-blue-50 p-3 text-center">
            <div class="text-secondary break-words text-[10px] font-semibold uppercase tracking-wide">Charge</div>
            <div class="mt-0.5 break-words text-sm font-bold text-blue-700" [innerHTML]="collectionData.charge | amount"></div>
          </div>
          <div class="min-w-0 rounded-lg border border-green-100 bg-green-50 p-3 text-center">
            <div class="text-secondary break-words text-[10px] font-semibold uppercase tracking-wide">Collection</div>
            <div class="mt-0.5 break-words text-sm font-bold text-green-700" [innerHTML]="collectionData.collection | amount"></div>
          </div>
          <div class="min-w-0 rounded-lg border border-red-100 bg-red-50 p-3 text-center">
            <div class="text-secondary break-words text-[10px] font-semibold uppercase tracking-wide">Receivables</div>
            <div class="mt-0.5 break-words text-sm font-bold text-red-700" [innerHTML]="collectionData.receivables | amount"></div>
          </div>
        </div>
        @if (collectionData.by_community?.length > 0) {
          <div class="mt-2 space-y-2">
            @for (c of collectionData.by_community; track $index) {
              <div>
                <div class="mb-0.5 flex items-center justify-between text-xs">
                  <span class="text-default max-w-[60%] truncate">{{ c.name }}</span>
                  <span class="font-semibold" [ngClass]="c.pct >= 80 ? 'text-green-600' : c.pct >= 50 ? 'text-yellow-600' : 'text-red-600'"> {{ c.pct }}% </span>
                </div>
                <div class="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <div class="h-full rounded-full transition-all duration-500" [ngClass]="c.pct >= 80 ? 'bg-green-500' : c.pct >= 50 ? 'bg-yellow-400' : 'bg-red-400'" [style.width.%]="c.pct"></div>
                </div>
              </div>
            }
          </div>
        }
      }
    </div>

    <!-- Budget vs Actual -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="text-secondary text-sm font-semibold uppercase tracking-wide">Budget vs Actual</h3>
      </div>
      @if (!hasBudgetPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingBudget) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (budgetData) {
        <div class="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div class="min-w-0 rounded-lg border border-blue-100 bg-blue-50 p-3 text-center">
            <div class="text-secondary break-words text-[10px] font-semibold uppercase tracking-wide">Total Budget</div>
            <div class="mt-0.5 break-words text-sm font-bold text-blue-700" [innerHTML]="budgetData.total_budget | amount"></div>
          </div>
          <div class="min-w-0 rounded-lg border border-orange-100 bg-orange-50 p-3 text-center">
            <div class="text-secondary break-words text-[10px] font-semibold uppercase tracking-wide">Actual Spend</div>
            <div class="mt-0.5 break-words text-sm font-bold text-orange-700" [innerHTML]="budgetData.total_actual | amount"></div>
          </div>
          <div class="min-w-0 rounded-lg border border-red-100 bg-red-50 p-3 text-center">
            <div class="text-secondary break-words text-[10px] font-semibold uppercase tracking-wide">Overspend Risk</div>
            <div class="mt-0.5 text-sm font-bold text-red-700">{{ budgetData.overspend_communities ?? 0 }}</div>
          </div>
        </div>
        @if (budgetData.by_community?.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Top Communities by Budget Consumption</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <!-- Header -->
          <div class="mb-0.5 grid grid-cols-[1fr_80px_80px_40px] px-2">
            <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Community</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Budget</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Actual</span>
            <span class="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">%</span>
          </div>
          <div class="divide-y divide-gray-50">
            @for (c of budgetData.by_community; track $index) {
              <div class="px-2 py-1.5 transition-colors hover:bg-gray-50">
                <div class="grid grid-cols-[1fr_80px_80px_40px] items-center">
                  <span class="min-w-0">
                    <span class="block truncate text-xs font-medium text-gray-700">{{ c.name }}</span>
                    @if (c.period) {
                      <span class="block truncate text-[10px] text-gray-400">{{ c.period }}</span>
                    }
                  </span>
                  <span class="text-right text-xs text-gray-500" [innerHTML]="c.budget | amount"></span>
                  <span class="text-right text-xs" [ngClass]="c.pct >= 100 ? 'font-semibold text-red-600' : 'text-gray-700'" [innerHTML]="c.actual | amount"></span>
                  <span class="text-right text-xs font-semibold" [ngClass]="c.pct >= 100 ? 'text-red-600' : c.pct >= 90 ? 'text-orange-600' : 'text-green-600'">{{ c.pct }}%</span>
                </div>
                <div class="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    class="h-full rounded-full transition-all duration-500"
                    [ngClass]="c.pct >= 100 ? 'bg-red-500' : c.pct >= 90 ? 'bg-orange-400' : 'bg-green-500'"
                    [style.width.%]="c.pct > 100 ? 100 : c.pct"
                  ></div>
                </div>
              </div>
            }
          </div>
        }
      }
    </div>

    <!-- Payment Methods -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-payment-methods-card [dashboardType]="dashboardType" [filters]="filters" [requiredPermission]="'accounting_receipts_view'" />
    </div>
  </div>

  <!-- Bottom Row: High-Value Overdue Receivables + Pending Approvals -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="text-secondary text-sm font-semibold uppercase tracking-wide">High-Value Overdue Receivables</h3>
        @if (!loadingOverdue && overdueData?.total > 0) {
          <span class="rounded-full px-2 py-0.5 text-xs font-semibold" [ngClass]="overdueData.total | countBadge">{{ overdueData.total }}</span>
        }
      </div>
      @if (!hasArPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingOverdue) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (overdueData?.items?.length > 0) {
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b">
                <th class="text-secondary py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide">Invoice #</th>
                <th class="text-secondary py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide">Community</th>
                <th class="text-secondary py-1.5 text-right text-[10px] font-semibold uppercase tracking-wide">Amount</th>
                <th class="text-secondary py-1.5 text-right text-[10px] font-semibold uppercase tracking-wide">Overdue (d)</th>
              </tr>
            </thead>
            <tbody>
              @for (item of overdueData.items; track $index) {
                <tr class="border-b border-gray-50 hover:bg-gray-50">
                  <td class="py-2 font-medium text-blue-600" data-nowrap>{{ item.invoice_no ?? item.id }}</td>
                  <td class="text-secondary py-2" data-auto-clamp="2">{{ item.community_name ?? '—' }}</td>
                  <td class="text-default py-2 text-right font-semibold" [innerHTML]="item.amount | amount"></td>
                  <td class="py-2 text-right">
                    <span class="rounded px-1.5 py-0.5 text-xs font-medium" [ngClass]="getAgingColor(item.days_overdue)"> {{ item.days_overdue }}</span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else if (!loadingOverdue) {
        <div class="text-secondary py-6 text-center text-sm">No overdue receivables</div>
      }
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-approval-queue-list [filters]="filters" [requiredPermission]="'accounting_bills_approve'" />
    </div>
  </div>

  <!-- Lower Row: Subscription Billing + Activity Timeline -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-subscription-billing-card [dashboardType]="dashboardType" [filters]="filters" [requiredPermission]="'subscription_bills_view'" />
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
    </div>
  </div>
</div>
